import { Controller } from "@hotwired/stimulus"

// Polls /version and compares the response against the version that was
// embedded in the page at load time.  When they differ a deploy has happened —
// show a toast-style notification so the user can refresh.
//
// Usage (on <body>):
//   data-controller="version-notification"
//   data-version-notification-loaded-version-value="abc1234..."
//   data-version-notification-poll-interval-value="180"
//   data-version-notification-version-url-value="/version"
//
export default class extends Controller {
  static values = {
    loadedVersion: String,
    pollInterval: { type: Number, default: 120 },   // seconds
    versionUrl: { type: String, default: "/version" }
  }

  connect() {
    // Don't poll if no version was embedded (e.g. test environments)
    if (!this.loadedVersionValue) return

    // Defer the first check — a deploy can't have happened between server
    // render and JS init, so avoid competing for Puma threads on page load
    this._poll = setInterval(() => this._checkVersion(), this.pollIntervalValue * 1000)
  }

  disconnect() {
    clearInterval(this._poll)
  }

  // ---- Actions (called from notification buttons) ----

  refresh() {
    window.location.reload()
  }

  dismiss() {
    const banner = document.getElementById("deploy-notification-banner")
    if (!banner) return

    banner.classList.add("deploy-notif-dismissed")
    setTimeout(() => banner.classList.add("deploy-notif-hidden"), 380)
  }

  // ---- Private ----

  async _checkVersion() {
    try {
      const response = await fetch(this.versionUrlValue)
      if (!response.ok) return

      const serverVersion = (await response.text()).trim()
      if (serverVersion && serverVersion !== this.loadedVersionValue) {
        this._showBanner()
        clearInterval(this._poll) // stop polling once detected
      }
    } catch {
      // Network error — ignore, we'll try again next interval
    }
  }

  _showBanner() {
    const banner = document.getElementById("deploy-notification-banner")
    if (!banner) return

    banner.classList.remove("deploy-notif-hidden", "deploy-notif-dismissed")
  }
}
